/**
 * Animation Component Wrappers
 * Reusable animated components for common UI patterns
 */

import React, { forwardRef, ReactNode, useEffect, useState } from 'react';
import { motion, AnimatePresence, HTMLMotionProps } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { useScrollAnimation, useIntersectionAnimation, usePerformanceMonitor } from './hooks';
import { useAnimationConfig, useReducedMotion } from './context';
import { createEntranceVariants, createStaggerVariants, createLoadingVariants } from './utils';

/**
 * AnimatedContainer - Container with entrance animations
 */
interface AnimatedContainerProps extends HTMLMotionProps<'div'> {
  children: ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  delay?: number;
  duration?: number;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
}

export const AnimatedContainer = forwardRef<HTMLDivElement, AnimatedContainerProps>(
  ({ 
    children, 
    direction = 'fade', 
    delay = 0, 
    duration,
    className = '',
    triggerOnScroll = true,
    threshold = 0.1,
    ...motionProps 
  }, ref) => {
    const config = useAnimationConfig();
    const isReducedMotion = useReducedMotion();
    const { trackAnimation } = usePerformanceMonitor();
    
    // Use scroll animation if enabled, otherwise show immediately
    const scrollAnimation = useScrollAnimation(threshold, true);
    const shouldUseScrollTrigger = triggerOnScroll && !isReducedMotion;
    
    const variants = createEntranceVariants(config, direction);
    
    // Override duration if provided
    if (duration) {
      variants.visible.transition = {
        ...variants.visible.transition,
        duration,
        delay
      };
    } else if (delay) {
      variants.visible.transition = {
        ...variants.visible.transition,
        delay
      };
    }

    useEffect(() => {
      trackAnimation.start();
      return () => trackAnimation.end();
    }, [trackAnimation]);

    if (isReducedMotion) {
      return (
        <div ref={ref} className={className}>
          {children}
        </div>
      );
    }

    return (
      <motion.div
        ref={shouldUseScrollTrigger ? scrollAnimation.ref : ref}
        className={className}
        variants={variants}
        initial="hidden"
        animate={shouldUseScrollTrigger ? scrollAnimation.controls : "visible"}
        {...motionProps}
      >
        {children}
      </motion.div>
    );
  }
);

AnimatedContainer.displayName = 'AnimatedContainer';

/**
 * StaggeredList - List with sequential item animations
 */
interface StaggeredListProps extends HTMLMotionProps<'div'> {
  children: ReactNode[];
  staggerDelay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  className?: string;
  itemClassName?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
}

export const StaggeredList = forwardRef<HTMLDivElement, StaggeredListProps>(
  ({ 
    children, 
    staggerDelay = 0.1, 
    direction = 'up',
    className = '',
    itemClassName = '',
    triggerOnScroll = true,
    threshold = 0.1,
    ...motionProps 
  }, ref) => {
    const config = useAnimationConfig();
    const isReducedMotion = useReducedMotion();
    const { trackAnimation } = usePerformanceMonitor();
    
    const scrollAnimation = useScrollAnimation(threshold, true);
    const shouldUseScrollTrigger = triggerOnScroll && !isReducedMotion;
    
    const containerVariants = createStaggerVariants(config, staggerDelay);
    const itemVariants = createEntranceVariants(config, direction);

    useEffect(() => {
      trackAnimation.start();
      return () => trackAnimation.end();
    }, [trackAnimation]);

    if (isReducedMotion) {
      return (
        <div ref={ref} className={className}>
          {children.map((child, index) => (
            <div key={index} className={itemClassName}>
              {child}
            </div>
          ))}
        </div>
      );
    }

    return (
      <motion.div
        ref={shouldUseScrollTrigger ? scrollAnimation.ref : ref}
        className={className}
        variants={containerVariants}
        initial="hidden"
        animate={shouldUseScrollTrigger ? scrollAnimation.controls : "visible"}
        {...motionProps}
      >
        {children.map((child, index) => (
          <motion.div
            key={index}
            className={itemClassName}
            variants={itemVariants}
          >
            {child}
          </motion.div>
        ))}
      </motion.div>
    );
  }
);

StaggeredList.displayName = 'StaggeredList';

/**
 * FadeInOnScroll - Component that fades in when scrolled into view
 */
interface FadeInOnScrollProps extends HTMLMotionProps<'div'> {
  children: ReactNode;
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  className?: string;
  fadeDirection?: 'up' | 'down' | 'left' | 'right' | 'fade';
  distance?: number;
}

export const FadeInOnScroll = forwardRef<HTMLDivElement, FadeInOnScrollProps>(
  ({ 
    children, 
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
    className = '',
    fadeDirection = 'up',
    distance = 30,
    ...motionProps 
  }, ref) => {
    const config = useAnimationConfig();
    const isReducedMotion = useReducedMotion();
    const { trackAnimation } = usePerformanceMonitor();
    
    const intersectionAnimation = useIntersectionAnimation({
      threshold,
      rootMargin
    });

    // Create custom variants based on fade direction and distance
    const variants = {
      hidden: {
        opacity: 0,
        ...(fadeDirection === 'up' && !isReducedMotion && { y: distance }),
        ...(fadeDirection === 'down' && !isReducedMotion && { y: -distance }),
        ...(fadeDirection === 'left' && !isReducedMotion && { x: distance }),
        ...(fadeDirection === 'right' && !isReducedMotion && { x: -distance }),
      },
      visible: {
        opacity: 1,
        y: 0,
        x: 0,
        transition: {
          duration: config.customTimings.normal,
          ease: config.customTimings.defaultEasing
        }
      }
    };

    useEffect(() => {
      if (intersectionAnimation.isIntersecting) {
        trackAnimation.start();
      } else {
        trackAnimation.end();
      }
    }, [intersectionAnimation.isIntersecting, trackAnimation]);

    if (isReducedMotion) {
      return (
        <div ref={ref} className={className}>
          {children}
        </div>
      );
    }

    return (
      <motion.div
        ref={intersectionAnimation.ref}
        className={className}
        variants={variants}
        initial="hidden"
        animate={intersectionAnimation.controls}
        {...motionProps}
      >
        {children}
      </motion.div>
    );
  }
);

FadeInOnScroll.displayName = 'FadeInOnScroll';

/**
 * LoadingSpinner - Animated loading spinner with smooth transitions
 */
interface LoadingSpinnerProps {
  isLoading: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  className?: string;
  children?: ReactNode; // Content to show when not loading
  loadingText?: string;
  spinnerType?: 'circular' | 'dots' | 'pulse' | 'bars';
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  isLoading,
  size = 'medium',
  color = 'currentColor',
  className = '',
  children,
  loadingText,
  spinnerType = 'circular'
}) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const { trackAnimation } = usePerformanceMonitor();

  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  const loadingVariants = createLoadingVariants(config);

  useEffect(() => {
    if (isLoading) {
      trackAnimation.start();
    } else {
      trackAnimation.end();
    }
  }, [isLoading, trackAnimation]);

  // Circular spinner
  const CircularSpinner = () => (
    <motion.div
      className={`inline-block ${sizeClasses[size]} ${className}`}
      animate={isReducedMotion ? {} : { rotate: 360 }}
      transition={isReducedMotion ? {} : {
        duration: 1,
        repeat: Infinity,
        ease: "linear"
      }}
    >
      <svg
        className="w-full h-full"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="31.416"
          strokeDashoffset="31.416"
          opacity="0.3"
        />
        <motion.circle
          cx="12"
          cy="12"
          r="10"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="31.416"
          initial={{ strokeDashoffset: 31.416 }}
          animate={isReducedMotion ? {} : { strokeDashoffset: [31.416, 0, 31.416] }}
          transition={isReducedMotion ? {} : {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </svg>
    </motion.div>
  );

  // Dots spinner
  const DotsSpinner = () => (
    <div className={`flex space-x-1 ${className}`}>
      {[0, 1, 2].map((index) => (
        <motion.div
          key={index}
          className={`${sizeClasses[size]} rounded-full`}
          style={{ backgroundColor: color }}
          animate={isReducedMotion ? {} : {
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={isReducedMotion ? {} : {
            duration: 0.6,
            repeat: Infinity,
            delay: index * 0.2,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );

  // Pulse spinner
  const PulseSpinner = () => (
    <motion.div
      className={`${sizeClasses[size]} rounded-full ${className}`}
      style={{ backgroundColor: color }}
      animate={isReducedMotion ? {} : {
        scale: [1, 1.1, 1],
        opacity: [0.7, 1, 0.7]
      }}
      transition={isReducedMotion ? {} : {
        duration: 1,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );

  // Bars spinner
  const BarsSpinner = () => (
    <div className={`flex space-x-1 ${className}`}>
      {[0, 1, 2, 3].map((index) => (
        <motion.div
          key={index}
          className="w-1 rounded-full"
          style={{ 
            backgroundColor: color,
            height: size === 'small' ? '16px' : size === 'medium' ? '24px' : '32px'
          }}
          animate={isReducedMotion ? {} : {
            scaleY: [1, 2, 1]
          }}
          transition={isReducedMotion ? {} : {
            duration: 0.8,
            repeat: Infinity,
            delay: index * 0.1,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );

  const spinnerComponents = {
    circular: CircularSpinner,
    dots: DotsSpinner,
    pulse: PulseSpinner,
    bars: BarsSpinner
  };

  const SpinnerComponent = spinnerComponents[spinnerType];

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="loading"
          className="flex flex-col items-center justify-center space-y-2"
          variants={loadingVariants}
          initial="loading"
          animate="loading"
          exit="loaded"
        >
          <SpinnerComponent />
          {loadingText && (
            <motion.p
              className="text-sm text-gray-600"
              animate={isReducedMotion ? {} : { opacity: [0.5, 1, 0.5] }}
              transition={isReducedMotion ? {} : {
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {loadingText}
            </motion.p>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="content"
          variants={loadingVariants}
          initial="loading"
          animate="loaded"
          exit="loading"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * AnimatedCounter - Number counter with smooth animation
 */
interface AnimatedCounterProps {
  value: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 2,
  decimals = 0,
  prefix = '',
  suffix = '',
  className = '',
  triggerOnScroll = true,
  threshold = 0.1
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const scrollAnimation = useScrollAnimation(threshold, true);
  const { trackAnimation } = usePerformanceMonitor();

  useEffect(() => {
    if (isReducedMotion) {
      setDisplayValue(value);
      return;
    }

    const shouldStart = triggerOnScroll ? scrollAnimation.isVisible : true;
    
    if (shouldStart) {
      trackAnimation.start();
      
      const startTime = Date.now();
      const startValue = displayValue;
      const endValue = value;
      const totalDuration = duration * 1000;

      const updateValue = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = startValue + (endValue - startValue) * easeOutQuart;
        
        setDisplayValue(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(updateValue);
        } else {
          trackAnimation.end();
        }
      };

      requestAnimationFrame(updateValue);
    }
  }, [value, duration, isReducedMotion, scrollAnimation.isVisible, triggerOnScroll, trackAnimation, displayValue]);

  const formattedValue = displayValue.toFixed(decimals);

  return (
    <span 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={className}
    >
      {prefix}{formattedValue}{suffix}
    </span>
  );
};

/**
 * AnimatedProgress - Progress bar with smooth animation
 */
interface AnimatedProgressProps {
  value: number; // 0-100
  height?: number;
  color?: string;
  backgroundColor?: string;
  className?: string;
  showLabel?: boolean;
  labelPosition?: 'inside' | 'outside';
  triggerOnScroll?: boolean;
  threshold?: number;
  duration?: number;
}

export const AnimatedProgress: React.FC<AnimatedProgressProps> = ({
  value,
  height = 8,
  color = '#3b82f6',
  backgroundColor = '#e5e7eb',
  className = '',
  showLabel = false,
  labelPosition = 'outside',
  triggerOnScroll = true,
  threshold = 0.1,
  duration = 1.5
}) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const scrollAnimation = useScrollAnimation(threshold, true);
  const { trackAnimation } = usePerformanceMonitor();

  const springProps = useSpring({
    width: triggerOnScroll ? (scrollAnimation.isVisible ? `${value}%` : '0%') : `${value}%`,
    config: {
      tension: config.customTimings.springConfig.tension,
      friction: config.customTimings.springConfig.friction,
      duration: isReducedMotion ? 0 : duration * 1000
    },
    onStart: () => trackAnimation.start(),
    onRest: () => trackAnimation.end()
  });

  return (
    <div 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`relative ${className}`}
    >
      <div
        className="w-full rounded-full overflow-hidden"
        style={{ height, backgroundColor }}
      >
        <animated.div
          className="h-full rounded-full transition-all duration-300"
          style={{
            backgroundColor: color,
            width: springProps.width
          }}
        />
      </div>
      {showLabel && (
        <div
          className={`text-sm font-medium ${
            labelPosition === 'inside' 
              ? 'absolute inset-0 flex items-center justify-center text-white' 
              : 'mt-1 text-gray-700'
          }`}
        >
          {value}%
        </div>
      )}
    </div>
  );
};